import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import { PaymentStatusBadge } from "@/components/admin/StatusBadge";
import {
  PaymentRow,
  computePaymentsSummary,
  loadPaymentRows,
  methodLabel,
  paymentStatusLabel,
  rowsToCsv,
} from "@/lib/paymentsApi";
import { formatDisplayDate } from "@/lib/bookingLabels";
import { formatGbpFromPence } from "@/types/database";
import { isSupabaseConfigured } from "@/lib/supabase";
import type { BookingStatus, PaymentStatus } from "@/types/database";

const PaymentsPage = () => {
  const [rows, setRows] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  useEffect(() => {
    document.title = "Payments · Olivia Flemming Admin";
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const res = await loadPaymentRows();
      if (!alive) return;
      if (res.ok === false) {
        setError(res.error);
        setRows([]);
      } else {
        setError(null);
        setRows(res.data);
      }
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (from && r.performanceDate < from) return false;
      if (to && r.performanceDate > to) return false;
      if (statusFilter === "paid" && r.paymentStatus !== "paid") return false;
      if (statusFilter === "outstanding" && !["awaiting", "attention"].includes(r.paymentStatus))
        return false;
      if (statusFilter === "attention" && r.paymentStatus !== "attention") return false;
      if (statusFilter === "cancelled" && r.bookingStatus !== "cancelled") return false;
      if (!needle) return true;
      return [r.venue, r.reference, r.method, r.paymentStatus]
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [rows, q, from, to, statusFilter]);

  const summary = useMemo(() => computePaymentsSummary(filtered), [filtered]);

  const cards = [
    { label: "Received (gross)", value: formatGbpFromPence(summary.receivedPence) },
    { label: "Outstanding", value: formatGbpFromPence(summary.outstandingPence) },
    { label: "Paid this month", value: formatGbpFromPence(summary.paidThisMonthPence) },
    { label: "Paid this year", value: formatGbpFromPence(summary.paidThisYearPence) },
    { label: "Expected upcoming", value: formatGbpFromPence(summary.expectedUpcomingPence) },
  ];

  const downloadCsv = () => {
    const csv = rowsToCsv(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `olivia-payments-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminShell
      title="Payments"
      actions={
        <button
          type="button"
          onClick={downloadCsv}
          disabled={filtered.length === 0}
          className="inline-flex items-center gap-2 rounded-full border border-primary/40 text-primary px-5 py-3 text-sm hover:bg-primary/10 disabled:opacity-40"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      }
    >
      {!isSupabaseConfigured && (
        <div className="mb-6 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100/90">
          Connect Supabase to load live payment figures.
        </div>
      )}
      {error && (
        <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      )}

      <p className="text-xs text-white/40 font-inter mb-6 max-w-3xl leading-relaxed">
        Figures are <strong className="text-white/60">gross booking fees</strong> recorded in the
        portal (pence → GBP). Stripe Dashboard payouts / bank deposits can differ after fees and
        payout schedules — treat Stripe payout reports as the source for cash in bank.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl border border-white/10 bg-secondary/60 p-4">
            <p className="text-[11px] uppercase tracking-widest text-white/45 font-inter mb-2">
              {c.label}
            </p>
            <p className="font-playfair text-2xl md:text-3xl">
              {loading ? "—" : c.value}
            </p>
          </div>
        ))}
      </div>

      <div className="flex flex-col lg:flex-row gap-3 mb-6">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search venue, ref, method…"
          className="flex-1 rounded-xl bg-secondary border border-white/10 px-4 py-3 text-sm text-white focus:outline-none focus:border-primary"
        />
        <input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="rounded-xl bg-secondary border border-white/10 px-4 py-3 text-sm text-white focus:outline-none focus:border-primary"
          aria-label="From date"
        />
        <input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="rounded-xl bg-secondary border border-white/10 px-4 py-3 text-sm text-white focus:outline-none focus:border-primary"
          aria-label="To date"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-xl bg-secondary border border-white/10 px-4 py-3 text-sm text-white focus:outline-none focus:border-primary"
        >
          <option value="all">All statuses</option>
          <option value="paid">Paid</option>
          <option value="outstanding">Outstanding</option>
          <option value="attention">Needs attention</option>
          <option value="cancelled">Cancelled bookings</option>
        </select>
      </div>

      {loading ? (
        <p className="text-white/50 text-sm">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-white/50 text-sm">No payments match these filters.</p>
      ) : (
        <>
        <ul className="md:hidden space-y-3">
          {filtered.map((r) => (
            <li key={r.bookingId}>
              <Link
                to={`/admin/bookings/${r.bookingId}`}
                className="block rounded-2xl border border-white/10 bg-white/[0.03] p-4 active:bg-white/[0.08] font-inter"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-base text-white truncate">{r.venue}</p>
                    <p className="text-xs text-white/45 mt-0.5">{r.reference} · {methodLabel(r.method)}</p>
                  </div>
                  <p className="text-base text-primary shrink-0">{formatGbpFromPence(r.feePence)}</p>
                </div>
                <div className="flex items-center justify-between gap-3 mt-3">
                  <p className="text-sm text-white/70">{formatDisplayDate(r.performanceDate)}</p>
                  <PaymentStatusBadge paymentStatus={r.paymentStatus as PaymentStatus} bookingStatus={r.bookingStatus as BookingStatus} />
                </div>
              </Link>
            </li>
          ))}
        </ul>
        <div className="hidden md:block overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full text-left text-sm font-inter min-w-[800px]">
            <thead className="bg-white/5 text-white/45 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Venue</th>
                <th className="px-4 py-3 font-medium">Ref</th>
                <th className="px-4 py-3 font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">Method</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.bookingId} className="border-t border-white/10 hover:bg-white/[0.03]">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <Link to={`/admin/bookings/${r.bookingId}`} className="hover:text-primary">
                      {formatDisplayDate(r.performanceDate)}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/admin/bookings/${r.bookingId}`} className="hover:text-primary">
                      {r.venue}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-white/50">{r.reference}</td>
                  <td className="px-4 py-3">{formatGbpFromPence(r.feePence)}</td>
                  <td className="px-4 py-3 text-white/70">{methodLabel(r.method)}</td>
                  <td className="px-4 py-3">
                    <PaymentStatusBadge
                      paymentStatus={r.paymentStatus as PaymentStatus}
                      bookingStatus={r.bookingStatus as BookingStatus}
                    />
                    <span className="sr-only">{paymentStatusLabel(r.paymentStatus)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </AdminShell>
  );
};

export default PaymentsPage;
